import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import DoctorShell from "@/components/DoctorShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Facebook, Instagram, Building2, Plug, Trash2, Send, Sparkles, Calendar, BarChart3, CheckCircle2, XCircle, ExternalLink, Info } from "lucide-react";
import api, { errMsg } from "@/lib/api";
const CHANNELS = [
    { key: "facebook", label: "Facebook Page", Icon: Facebook, color: "#1877F2" },
    { key: "instagram", label: "Instagram Business", Icon: Instagram, color: "#E4405F" },
    { key: "gmb", label: "Google Business Profile", Icon: Building2, color: "#4285F4" },
];

const DemoBadge = () => (
    <Badge variant="outline" className="border-[#D97706] text-[#D97706] text-[10px] uppercase tracking-widest" data-testid="social-demo-badge">Demo Mode</Badge>
);
const LiveBadge = () => (
    <Badge className="bg-[#15803D] text-white text-[10px] uppercase tracking-widest" data-testid="social-live-badge">Live</Badge>
);

export default function DoctorSocial() {
    const [search] = useSearchParams();
    const [metaStatus, setMetaStatus] = useState(null);
    useEffect(() => {
        api.get("/social/meta/status").then((r) => setMetaStatus(r.data)).catch(() => setMetaStatus({ mode: "demo" }));
        // Handle callback results
        const err = search.get("meta_error");
        const ok = search.get("meta");
        if (err) toast.error(`Meta connect failed: ${err.replace(/_/g, " ")}`);
        else if (ok === "connected") toast.success("Facebook + Instagram connected via Meta");
    }, [search]);
    const metaLive = metaStatus?.mode === "live";
    return (
        <DoctorShell>
            <div className="max-w-5xl mx-auto py-8 px-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <h1 className="text-2xl font-bold text-[#123B5D]" data-testid="social-page-title">Social &amp; Local Presence</h1>
                    {metaLive ? <LiveBadge /> : <DemoBadge />}
                </div>
                {metaLive && metaStatus?.dev_mode_notice && (
                    <div className="mb-4 rounded-lg border border-[#D97706]/30 bg-[#D97706]/5 p-3 text-[12px] text-[#17212B]/80 flex items-start gap-2" data-testid="meta-dev-mode-banner">
                        <Info size={14} className="text-[#D97706] mt-0.5 shrink-0" />
                        <span>
                            <strong className="text-[#D97706]">Meta Dev Mode:</strong>{" "}{metaStatus.dev_mode_notice}
                        </span>
                    </div>
                )}
                <p className="text-sm text-[#17212B]/60 mb-6">
                    Connect Facebook, Instagram and Google Business Profile. Currently in <strong>Demo mode</strong> — connections and posts simulated. Real API activation via Super Admin → Integrations.
                </p>
                <Tabs defaultValue="connections">
                    <TabsList data-testid="social-tabs">
                        <TabsTrigger value="connections" data-testid="tab-connections"><Plug size={14} className="mr-1" />Connections</TabsTrigger>
                        <TabsTrigger value="composer" data-testid="tab-composer"><Send size={14} className="mr-1" />Composer</TabsTrigger>
                        <TabsTrigger value="posts" data-testid="tab-posts"><Calendar size={14} className="mr-1" />Posts</TabsTrigger>
                        <TabsTrigger value="insights" data-testid="tab-insights"><BarChart3 size={14} className="mr-1" />Insights</TabsTrigger>
                        <TabsTrigger value="gmb" data-testid="tab-gmb">GMB Readiness</TabsTrigger>
                    </TabsList>
                    <TabsContent value="connections"><ConnectionsTab /></TabsContent>
                    <TabsContent value="composer"><ComposerTab /></TabsContent>
                    <TabsContent value="posts"><PostsTab /></TabsContent>
                    <TabsContent value="insights"><InsightsTab /></TabsContent>
                    <TabsContent value="gmb"><GMBReadinessTab /></TabsContent>
                </Tabs>
            </div>
        </DoctorShell>
    );
}

function ConnectionsTab() {
    const [conns, setConns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const load = async () => {
        try {
            const r = await api.get("/social/connections");
            setConns(r.data.connections || []);
        } catch (e) { toast.error("Failed to load connections"); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        load();
        // Surface OAuth return-URL params one time.
        const p = new URLSearchParams(window.location.search);
        const gmbErr = p.get("gmb_error");
        const gmbConn = p.get("connected") === "gmb" ? p.get("status") : null;
        const metaErr = p.get("meta_error");
        if (gmbErr) {
            const map = {
                access_denied: "You cancelled — or your Google account isn't on the Testing users list yet.",
                invalid_state: "OAuth session expired. Please try connecting again.",
                expired_state: "OAuth session expired. Please try connecting again.",
                missing_code_or_state: "Google didn't return a valid response. Try again.",
                token_exchange_failed: "Google rejected the code exchange — check the OAuth client is not disabled.",
            };
            toast.error(map[gmbErr] || `Google connect failed: ${gmbErr}`, { duration: 8000 });
        } else if (gmbConn === "pending_access") {
            toast.warning(
                "Google account connected. Business Profile API access is pending Google approval — features will activate once granted.",
                { duration: 10000 }
            );
        } else if (gmbConn === "connected") {
            toast.success("Google Business Profile connected!");
        } else if (metaErr) {
            toast.error(`Meta connect failed: ${metaErr}`, { duration: 8000 });
        }
        if (gmbErr || gmbConn || metaErr) {
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);
    const connect = async (channel) => {
        setBusy(channel);
        try {
            // GBP/GMB live OAuth — mirror of the Meta pattern.
            if (channel === "gmb") {
                try {
                    const start = await api.get("/social/oauth/gmb/start");
                    if (start.data?.testing_mode_notice) {
                        toast.info(start.data.testing_mode_notice, { duration: 7000 });
                    }
                    window.location.assign(start.data.authorize_url);
                    return;
                } catch (e) {
                    if (e?.response?.data?.detail?.code === "gbp_not_live") {
                        // Fall through to legacy demo-mode connect below.
                    } else {
                        throw e;
                    }
                }
            }
            const r = await api.post(`/social/connections/${channel}/connect`, {});
            if (r.data?.needs_oauth) {
                const start = await api.get("/social/oauth/meta/start");
                if (start.data?.dev_mode_notice) {
                    toast.info(start.data.dev_mode_notice, { duration: 6000 });
                }
                window.location.assign(start.data.authorize_url);
                return;
            }
            toast.success(`${channel} connected (Demo)`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Connect failed");
        } finally { setBusy(""); }
    };
    const disconnect = async (channel) => {
        if (!window.confirm(`Disconnect ${channel}?`)) return;
        try {
            await api.post(`/social/connections/${channel}/disconnect`);
            toast.success("Disconnected");
            load();
        } catch (e) { toast.error("Disconnect failed"); }
    };
    return (
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
            {CHANNELS.map(({ key, label, Icon, color }) => {
                const conn = conns.find((c) => c.channel === key);
                return (
                    <div key={key} className="ddi-card p-5" data-testid={`conn-card-${key}`}>
                        <div className="flex items-center gap-2"><Icon size={20} style={{ color }} /><div className="font-semibold text-[#123B5D]">{label}</div></div>
                        {loading ? (
                            <div className="mt-3 space-y-2" data-testid={`conn-skeleton-${key}`}>
                                <div className="h-3 w-24 rounded bg-[#123B5D]/10 animate-pulse" />
                                <div className="h-3 w-40 rounded bg-[#123B5D]/10 animate-pulse" />
                                <div className="h-7 w-20 mt-2 rounded-full bg-[#123B5D]/10 animate-pulse" />
                            </div>
                        ) : conn ? (
                            <>
                                {conn.channel === "gmb" && conn.status === "connected_pending_api_access" ? (
                                    <div className="mt-3 text-xs text-[#D97706] font-semibold" data-testid={`conn-status-${key}`}>Connected · API access pending</div>
                                ) : conn.demo === false ? (
                                    <div className="mt-3 text-xs text-[#15803D] font-semibold" data-testid={`conn-status-${key}`}>Connected (Live)</div>
                                ) : (
                                    <div className="mt-3 text-xs text-[#15803D] font-semibold" data-testid={`conn-status-${key}`}>Connected (Demo)</div>
                                )}
                                <div className="text-sm mt-1 text-[#17212B]/80">
                                    {conn.account?.display_name}
                                    {conn.account?.followers != null ? ` · ${conn.account.followers} followers` : ""}
                                </div>
                                {conn.channel === "gmb" && conn.status === "connected_pending_api_access" && (
                                    <div className="mt-2 text-[11px] text-[#78350F] bg-[#FEF3C7] border border-[#D97706]/30 rounded p-2" data-testid="gmb-pending-notice">
                                        Google Business Profile API access is pending Google approval (typically 1–3 weeks).
                                        Features will activate automatically once approved — no action needed here.
                                    </div>
                                )}
                                <button onClick={() => disconnect(key)} className="mt-4 text-xs text-[#DC2626] font-semibold inline-flex items-center gap-1 hover:underline" data-testid={`disconnect-${key}`}><Trash2 size={12} />Disconnect</button>
                            </>
                        ) : (
                            <>
                                <div className="mt-3 text-xs text-[#17212B]/60">Not connected</div>
                                <button disabled={busy === key} onClick={() => connect(key)} className="mt-4 ddi-teal-btn text-xs" data-testid={`connect-${key}`}>{busy === key ? "Connecting…" : "Connect"}</button>
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ComposerTab() {
    const [text, setText] = useState("");
    const [channels, setChannels] = useState([]);
    const [busy, setBusy] = useState(false);
    const [assistBusy, setAssistBusy] = useState(false);
    const [suggestions, setSuggestions] = useState(null);
    const [topic, setTopic] = useState("");
    const toggle = (c) => setChannels((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);

    async function aiAssist() {
        const t = (topic || text).trim();
        if (!t) return toast.error("Enter a topic or draft text first");
        const platforms = channels.length ? channels : ["facebook", "instagram", "gmb"];
        setAssistBusy(true);
        try {
            const r = await api.post("/social/caption-assist", { topic: t, platforms });
            setSuggestions(r.data.captions || {});
            if (r.data.fallback) toast.warning("AI unavailable — used deterministic template");
            else toast.success("Captions generated");
        } catch (e) {
            toast.error(errMsg(e, "AI Assist failed"));
        } finally {
            setAssistBusy(false);
        }
    }

    const submit = async (action) => {
        if (!text.trim()) return toast.error("Add post text first");
        if (!channels.length) return toast.error("Select at least one channel");
        setBusy(true);
        try {
            await api.post("/social/posts", { text, channels, action });
            toast.success(action === "post_now" ? "Posted (Demo)" : `${action === "schedule" ? "Scheduled" : "Draft"} saved`);
            setText("");
            setSuggestions(null);
            setTopic("");
        } catch (e) { toast.error(e?.response?.data?.detail?.message || "Failed"); }
        finally { setBusy(false); }
    };
    return (
        <div className="mt-4 ddi-card p-5 space-y-4" data-testid="composer-panel">
            <div>
                <Label>Topic (for AI Assist — optional)</Label>
                <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                       placeholder="e.g. Preventing hypertension in adults" data-testid="composer-topic" />
            </div>
            <div>
                <Label>Post content</Label>
                <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)}
                          placeholder="Write your post caption… or click AI Assist to generate" data-testid="composer-text" />
            </div>
            <div>
                <Label>Target channels</Label>
                <div className="flex gap-4 mt-2">
                    {CHANNELS.map(({ key, label }) => (
                        <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={channels.includes(key)} onCheckedChange={() => toggle(key)} data-testid={`composer-channel-${key}`} />
                            {label}
                        </label>
                    ))}
                </div>
            </div>

            {suggestions ? (
                <div className="rounded-lg border border-[#0F766E]/30 bg-[#0F766E]/5 p-3 space-y-2" data-testid="ai-suggestions">
                    <div className="text-xs uppercase tracking-widest text-[#0F766E] font-semibold">AI suggestions — click to use</div>
                    {Object.entries(suggestions).map(([platform, caption]) => (
                        <button key={platform}
                                onClick={() => { setText(caption); toast.info(`Loaded ${platform} caption`); }}
                                className="w-full text-left rounded border border-[#0F766E]/20 bg-white p-2 hover:border-[#0F766E]"
                                data-testid={`ai-suggestion-${platform}`}>
                            <div className="text-[10px] uppercase tracking-widest text-[#0F766E]">{platform}</div>
                            <div className="text-sm text-[#17212B] whitespace-pre-wrap">{caption}</div>
                        </button>
                    ))}
                </div>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-2">
                <button className="text-sm text-[#0F766E] font-semibold disabled:opacity-50" data-testid="composer-ai-assist"
                        onClick={aiAssist} disabled={assistBusy}>
                    <Sparkles size={12} className="inline mr-1" />{assistBusy ? "Generating…" : "AI Assist"}
                </button>
                <div className="flex-1" />
                <button disabled={busy} onClick={() => submit("draft")} className="rounded-full border border-[#123B5D] text-[#123B5D] text-sm px-4 py-1.5 hover:bg-[#123B5D] hover:text-white" data-testid="composer-save-draft">Save Draft</button>
                <button disabled={busy} onClick={() => submit("schedule")} className="rounded-full border border-[#123B5D] text-[#123B5D] text-sm px-4 py-1.5 hover:bg-[#123B5D] hover:text-white" data-testid="composer-schedule">Schedule</button>
                <button disabled={busy} onClick={() => submit("post_now")} className="ddi-teal-btn text-sm" data-testid="composer-post-now">Post now (Demo)</button>
            </div>
        </div>
    );
}

function PostsTab() {
    const [posts, setPosts] = useState([]);
    const load = async () => { try { const r = await api.get("/social/posts"); setPosts(r.data.posts || []); } catch (e) { /* noop */ } };
    useEffect(() => { load(); }, []);
    const del = async (id) => { if (!window.confirm("Delete this post?")) return; try { await api.delete(`/social/posts/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e, "Failed")); } };
    return (
        <div className="mt-4 space-y-3" data-testid="posts-list">
            {posts.length === 0 ? <div className="text-sm text-[#17212B]/60">No posts yet. Head to Composer to create one.</div> : posts.map((p) => (
                <div key={p.id} className="ddi-card p-4" data-testid={`post-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#17212B]/60">{p.status} · {p.channels.join(", ")} <DemoBadge /></div>
                            <div className="mt-2 text-sm text-[#17212B] whitespace-pre-wrap">{p.text}</div>
                        </div>
                        {p.status === "draft" || p.status === "scheduled" ? (
                            <button onClick={() => del(p.id)} className="text-[#DC2626] text-xs" data-testid={`post-delete-${p.id}`}><Trash2 size={14} /></button>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
}

function InsightsTab() {
    const [data, setData] = useState({});
    const load = async () => {
        const out = {};
        for (const { key } of CHANNELS) {
            try { const r = await api.get(`/social/insights/${key}`); out[key] = r.data; } catch (e) { out[key] = { connected: false }; }
        }
        setData(out);
    };
    useEffect(() => { load(); }, []);
    return (
        <div className="mt-4" data-testid="insights-panel">
            <div className="rounded-lg bg-[#D97706]/10 border border-[#D97706]/30 p-3 text-sm text-[#17212B] mb-4">
                <strong>Demo data</strong> — connect real accounts (via Super Admin) to see actual insights.
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
                {CHANNELS.map(({ key, label, Icon, color }) => {
                    const d = data[key] || {};
                    return (
                        <div key={key} className="ddi-card p-5" data-testid={`insights-${key}`}>
                            <div className="flex items-center gap-2"><Icon size={16} style={{ color }} /><div className="font-semibold text-[#123B5D]">{label}</div></div>
                            {d.connected ? (
                                <div className="mt-3 space-y-1 text-sm">
                                    <div>Followers: <strong>{d.followers}</strong></div>
                                    <div>Reach (28d): <strong>{d.reach}</strong></div>
                                    <div>Impressions: <strong>{d.impressions}</strong></div>
                                    <div>Engagement: <strong>{d.engagement_rate}%</strong></div>
                                </div>
                            ) : <div className="mt-3 text-sm text-[#17212B]/60">Connect this channel to see insights.</div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function GMBReadinessTab() {
    const [state, setState] = useState(null);
    useEffect(() => { api.get("/social/gmb-readiness").then((r) => setState(r.data)).catch(() => {}); }, []);
    if (!state) return <div className="mt-4 text-sm text-[#17212B]/60">Loading…</div>;
    return (
        <div className="mt-4 space-y-3" data-testid="gmb-readiness">
            <div className="text-sm">
                <strong>{state.passed}/{state.total}</strong> checks passed. <a href={state.external_link} target="_blank" rel="noreferrer" className="text-[#0F766E] font-semibold inline-flex items-center gap-1">Open Google Business Profile Manager <ExternalLink size={11} /></a>
            </div>
            <ul className="ddi-card divide-y divide-[#123B5D]/10">
                {state.checks.map((c) => (
                    <li key={c.key} className="p-3 flex items-center gap-3" data-testid={`gmb-check-${c.key}`}>
                        {c.passed ? <CheckCircle2 size={16} className="text-[#15803D]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                        <div className="flex-1 text-sm">{c.label}</div>
                        {!c.passed && c.fix_link ? <a href={c.fix_link} className="text-xs text-[#0F766E] font-semibold hover:underline">{c.hint || "Fix"} →</a> : null}
                    </li>
                ))}
            </ul>
        </div>
    );
}
